import { useContext, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Breadcrumb, Entry, Icon, ListChooser, Page, Pill, Row } from '../../components';
import { AuthContext, SettingsContext, ToastContext } from '../../context';
import { ActiveListContext } from '../../context/ActiveListContext';
import { SCHOOLS, SPELLS } from '../../data';
import { useWideMode } from '../../hooks';
import { classNames, subclassNames, componentsDisplay, durationDisplay, getSpellId, isConc, levelDisplay, materialComponent, rangeDisplay, timeDisplay, removeMacros } from '../../util/spell';
import './SpellDisplayPage.scss';
import { Button } from '@mui/material';

export const SpellDisplayPage = () => {
    const isWide = useWideMode();
    const { toast } = useContext(ToastContext);
    const { activeList, addSpell, hasSpell, removeSpell } = useContext(ActiveListContext);
    const { includeSubclasses } = useContext(SettingsContext);
    const params = useParams();
    const [ searchParams ] = useSearchParams();
    const [ spell, setSpell ] = useState(null);
    const [ listChooserOpen, setListChooserOpen ] = useState(false);
    const { isLoggedIn } = useContext(AuthContext);

    const spellId = isWide ? searchParams.get('spellId') : params.spellId;

    useEffect(() => {
      setSpell(SPELLS.find(s => getSpellId(s) === spellId));
    }, [spellId]);

    const attackTypes = {
        M: 'Melee',
        R: 'Ranged'
    };

    return (
        <Page className="SpellDisplayPage">
            <ListChooser open={listChooserOpen} onClose={() => setListChooserOpen(false)} spellId={spellId} />
            <Breadcrumb
                action={isLoggedIn &&
                    <Button 
                        variant='contained' 
                        color='secondary'
                        onClick={() => setListChooserOpen(true)}
                    >
                        <Icon name='plus' size='small' />
                        &nbsp;Add to a list
                    </Button>
                }
            />
            { !!spell ? <>
                <Row className='pageheader'>
                    <div className='pageTitleWrap'>
                        <h2>{spell?.name}</h2>
                        <div className='metaLine'>
                            <span>{levelDisplay(spell?.level)} {SCHOOLS[spell?.school]}</span>
                            {spell?.meta?.ritual && <Pill className='ritual'>ritual</Pill>}
                        </div>
                    </div>
                    <div className='headerActions'>
                        <Pill className='sourcePill'>{spell?.source} p{spell?.page}</Pill>
                        <button
                            type='button'
                            className='shareButton'
                            onClick={() => {
                                navigator.clipboard.writeText(window.location.href);
                                toast('Link copied to clipboard!');
                            }}
                            aria-label='Copy spell link'
                        >
                            <Icon name='share-nodes' size='small' />
                        </button>
                    </div>
                </Row>

                {activeList && (
                    hasSpell(spell) ? 
                        <Row onClick={() => removeSpell(spell)} >
                            <Pill className="active">
                                Added to list
                            </Pill>
                        </Row>
                    :
                        <Row onClick={() => addSpell(spell)} >
                            <Pill>
                                + Add to list
                            </Pill>
                        </Row>
                )}

                <div className='detailSection'>
                    <div className='sectionLabel'>School</div>
                    <Row className='chipRow'>
                        <Pill>{levelDisplay(spell?.level)} {SCHOOLS[spell?.school]}</Pill>
                        {spell?.meta?.ritual && <Pill>ritual</Pill>}
                    </Row>
                </div>

                <div className='detailSection'>
                    <div className='sectionLabel'>Classes</div>
                    <Row className='chipRow'>
                        {classNames(spell?.classes, false).map(n => <Pill key={n}>{n}</Pill>)}
                    </Row>
                </div>

                {(includeSubclasses && !!subclassNames(spell?.classes).length) && <div className='detailSection'>
                    <div className='sectionLabel'>Subclasses</div>
                    <Row className='chipRow'>
                        {subclassNames(spell?.classes).map(n => <Pill key={n}>{n}</Pill>)}
                    </Row>
                </div>}

                <div className='detailSection'>
                    <div className='sectionLabel'>Effects</div>
                    <Row className='chipRow'>
                        {(spell?.savingThrow || []).map(n => <Pill key={n}>{n} save</Pill>)}
                        {(spell?.damageInflict || []).map(n => <Pill key={n}>{n} damage</Pill>)}
                        {(spell?.spellAttack || []).map(n => 
                            <Pill key={n}>{attackTypes[n]} spell attack</Pill>
                        )}
                        {(spell?.conditionInflict || []).map(n => 
                            <Pill key={n}>{n} condition</Pill>
                        )}
                    </Row>
                </div>

                <div className='detailSection statSection'>
                    <div className='sectionLabel'>Details</div>
                    <div className='statGrid'>
                        <div className='statItem'>
                            <span className='statLabel'>Casting time</span>
                            <span className='statValue'>{removeMacros(timeDisplay(spell?.time))}</span>
                        </div>
                        <div className='statItem'>
                            <span className='statLabel'>Range</span>
                            <span className='statValue'>{rangeDisplay(spell?.range)}</span>
                        </div>
                        <div className='statItem'>
                            <span className='statLabel'>Components</span>
                            <span className='statValue'>{componentsDisplay(spell?.components)}</span>
                        </div>
                        <div className='statItem'>
                            <span className='statLabel'>Duration</span>
                            <span className='statValue'>{isConc(spell) && 'Concentration, up to '} {durationDisplay(spell?.duration)}</span>
                        </div>
                    </div>
                    { spell?.components?.m && 
                        <div className='materialNote'>*{materialComponent(spell?.components)}</div>
                    }
                </div>

                {spell?.entries.map((e, i) => <Entry key={i} entry={e} />)}
                {!!spell?.entriesHigherLevel && (spell.entriesHigherLevel).map((e, i) => <Entry key={i} entry={e} />)}
            </> :
                !isWide && <Row>Spell not found</Row>
            }
        </Page>
    );
};